// SPDX-License-Identifier: AGPL-3.0-or-later

import type {ScrollerHandle} from '@app/features/ui/components/Scroller';
import {type RefObject, useCallback, useEffect, useLayoutEffect, useState} from 'react';

// Micup: how far below the scroller's top a virtual list starts when other content scrolls above it in the same
// scroller (its scrollMargin). Measured again whenever observedRef's element (what sits above the list, or the whole
// page) changes size.
export function useScrollMargin(
	listRef: RefObject<HTMLElement | null>,
	observedRef: RefObject<HTMLElement | null>,
	scrollerRef: RefObject<Pick<ScrollerHandle, 'getViewportElement'> | null>,
): number {
	const [scrollMargin, setScrollMargin] = useState(0);
	const measure = useCallback(() => {
		const list = listRef.current;
		const viewport = scrollerRef.current?.getViewportElement();
		if (!list || !viewport) return false;
		// Under a CSS zoom (the desktop feed) client rects are in screen pixels and scrollTop in the zoomed page's.
		const zoom = viewport.currentCSSZoom || 1;
		setScrollMargin(
			Math.round((list.getBoundingClientRect().top - viewport.getBoundingClientRect().top) / zoom + viewport.scrollTop),
		);
		return true;
	}, [listRef, scrollerRef]);
	// Before the first paint when the scroller is inside the measuring component (the feed): its handle is set by now.
	// Inside the scroller (the profile page) the handle comes after this component's layout effects; the effect measures.
	useLayoutEffect(() => {
		measure();
	}, [measure]);
	useEffect(() => {
		const observed = observedRef.current;
		if (!observed || !measure()) return;
		const ownerWindow = observed.ownerDocument.defaultView ?? window;
		if (typeof ownerWindow.ResizeObserver === 'undefined') return;
		const observer = new ownerWindow.ResizeObserver(measure);
		observer.observe(observed);
		return () => observer.disconnect();
	}, [measure, observedRef]);
	return scrollMargin;
}
